import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Arrow from "images/left-arrow.svg";
import Modal from "./modal";
import { debounce } from "helpers/utility-helpers";

const ModalWithNavigation = ({
  className = "",
  modalContainerClassName = "",
  modalBackgroundClassName = "",
  children,
  isOpen = false,
  onClose = () => {},
  dataTestId = "modal-with-navigation",
  index,
  setIndex,
  length,
  onForward = () => {},
  onBack = () => {},
}) => {
  const modalRef = useRef(null);
  const leftButtonRef = useRef(null);
  const rightButtonRef = useRef(null);
  const browser = useSelector(state => state.browser);
  const isMobileViewport = browser?.isMobileViewport;

  const scrollToChild = index => {
    if (modalRef.current) {
      const childWidth = modalRef.current.firstChild?.offsetWidth || 0;
      modalRef.current.scrollLeft = index * childWidth;
    }
  };

  useEffect(() => {
    if (isOpen && isMobileViewport && index && modalRef.current) {
      scrollToChild(index);
    }
  }, [isOpen, isMobileViewport]);

  const navigate = direction => {
    if (direction === "left" && index > 0) {
      setIndex(index - 1);
      onBack();
    } else if (direction === "right" && index < length - 1) {
      setIndex(index + 1);
      onForward();
    }
  };

  const handleScroll = debounce(() => {
    if (isMobileViewport && modalRef.current) {
      const childWidth = modalRef.current.firstChild?.offsetWidth || 0;
      const newIndex = Math.round(modalRef.current.scrollLeft / childWidth);
      setIndex(newIndex);
    }
  }, 100);

  useEffect(() => {
    if (isMobileViewport && modalRef.current) {
      modalRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      modalRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, [isMobileViewport]);

  const renderButton = direction => {
    if (isMobileViewport) return null;

    const isDisabled = (direction === "left" && index === 0) || (direction === "right" && index === length - 1);

    return (
      <button
        onClick={() => navigate(direction)}
        disabled={isDisabled}
        ref={direction === "left" ? leftButtonRef : rightButtonRef}
        aria-label={`Navigate ${direction}`}
        data-test-id={`navigation-button-${direction}`}
        className={`p-8 pointer-events-auto ${direction === "left" ? "pr-4" : "pl-4"} ${
          isDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}>
        <Arrow
          className={`h-8 w-8 fill-lighterGray stroke-lighterGray stroke-1 ${direction === "left" ? "" : "rotate-180"}`}
        />
      </button>
    );
  };

  return (
    <Modal
      ref={modalRef}
      className={`${isMobileViewport ? "flex w-full overflow-x-auto snap-x snap-mandatory" : ""} ${className}`}
      modalContainerClassName={modalContainerClassName}
      modalBackgroundClassName={modalBackgroundClassName}
      isOpen={isOpen}
      onClose={onClose}
      dataTestId={dataTestId}
      leftButton={renderButton("left")}
      rightButton={renderButton("right")}>
      {children}
    </Modal>
  );
};

ModalWithNavigation.propTypes = {
  className: PropTypes.string,
  modalContainerClassName: PropTypes.string,
  modalBackgroundClassName: PropTypes.string,
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  dataTestId: PropTypes.string,
  index: PropTypes.number,
  setIndex: PropTypes.func,
  length: PropTypes.number,
  onForward: PropTypes.func,
  onBack: PropTypes.func,
};

export default ModalWithNavigation;
